/**
 * CustomerDetail — TASK-021 / flow-03, flow-05
 * Actor: All roles (read); Sales Rep / Office Admin (write)
 * Route: /customers/:id
 * API: GET /api/v1/customers/:id, contracts, payments, dmrv
 * States: loading / 404 / error / success (SOTA §3)
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  AppBadge, AppButton, AppCard, AppField,
  AppInput, AppModal, AppSelect, AppTable, ConfirmAction,
} from "../../../shared/components/ui";
import {
  IconCheck, IconChevronRight, IconError, IconInfo, IconSpinner, IconWarning,
} from "../../../shared/components/icons";
import { api } from "../../../shared/api/client";
import { getUser, hasPermission } from "../../auth/session";
import { withEntityQuery } from "../../../shared/navigation/entityPrefill";

// ─── Types ────────────────────────────────────────────────────────────────────

type Device = {
  id: number;
  serial_number: string;
  status: string;
  is_locked: boolean;
};

type Contract = {
  id: number;
  contract_number: string;
  total_price: number;
  final_price?: number;
  amount_paid: number;
  amount_remaining: number;
  arrears: number;
  payment_frequency?: string;
  is_completed: boolean;
  is_draft?: boolean;
  discount_percent?: number;
  discount_rule_id?: number | null;
  advance_required?: boolean;
  initial_deposit?: number;
  created_at: string;
  expected_end_date: string | null;
};

type ActiveContract = {
  id: number;
  contract_number: string;
  total_price: number;
  final_price?: number;
  amount_paid: number;
  amount_remaining: number;
  arrears: number;
  is_completed: boolean;
  is_draft?: boolean;
  discount_percent?: number;
  discount_rule_id?: number | null;
  advance_required?: boolean;
  initial_deposit?: number;
  expected_payment_amount?: number;
  payment_period_months?: number;
  start_date?: string | null;
  period_end_date?: string | null;
};

type DraftContract = {
  id: number;
  contract_number: string;
  is_draft: boolean;
  is_completed?: boolean;
};

type SourceLead = {
  id: number;
  status?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  converted_customer_id?: number | null;
};

type Payment = {
  id: number;
  payment_reference: string;
  amount: number;
  method: string;
  status: string;
  created_at: string;
  service_days?: number | null;
  token_days?: number | null;
  shortfall_cents?: number | null;
  remaining_for_token_tzs?: number | null;
  token_eligible?: boolean | null;
};

type DMRVStep = {
  step: number;
  label: string;
  status: string;
  actioned_by_id: number | null;
  actioned_at: string | null;
};

type CustomerData = {
  id: number;
  customer_number: string;
  first_name: string;
  last_name: string;
  phone: string;
  national_id: string | null;
  email: string | null;
  address: string | null;
  village?: string | null;
  ward: string | null;
  district: string | null;
  region: string | null;
  area_code?: string | null;
  is_active: boolean;
  dmrv_status: string;
  created_at: string;
  devices: Device[];
  active_contract?: ActiveContract | null;
  draft_contract?: DraftContract | null;
  source_lead?: SourceLead | null;
  total_paid?: number;
};

type Tab = "overview" | "contracts" | "payments" | "dmrv" | "credit";

function fmtMoney(value: number | null | undefined): string {
  return (value ?? 0).toLocaleString();
}

function deriveActiveDevice(devices: Device[]): Device | null {
  if (!devices.length) return null;
  const active = devices.find((d) => d.status !== "decommissioned");
  return active ?? devices[0];
}

// ─── Contract creation modal ──────────────────────────────────────────────────

const DEPOSIT_BY_CAPACITY: Record<number, number> = {
  50: 50_000,
  80: 80_000,
  100: 100_000,
};

type DiscountRuleOpt = { id: number; name: string; discount_percent: number };

function computeInstallment(
  finalPrice: number,
  months: number,
  deposit: number,
  frequency: string,
): number {
  const financeable = Math.max(Math.round((finalPrice - deposit) * 100) / 100, 0);
  const m = Math.max(months || 1, 1);
  let periods = m;
  const freq = (frequency || "monthly").toLowerCase();
  if (freq === "daily") periods = m * 30;
  else if (freq === "weekly") periods = m * 4;
  periods = Math.max(periods, 1);
  return Math.round((financeable / periods) * 100) / 100;
}

function CreateContractModal({
  customerId,
  devices,
  open,
  onClose,
  onSuccess,
  draftContractId,
  mode = "create",
}: {
  customerId: number;
  devices: Device[];
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  draftContractId?: number | null;
  mode?: "create" | "activate";
}) {
  const [form, setForm] = useState({
    device_id: "",
    total_price: "",
    discount_rule_id: "",
    advance_required: "yes",
    initial_deposit: "",
    payment_frequency: "monthly",
    duration_months: "12",
  });
  const [rules, setRules] = useState<DiscountRuleOpt[]>([]);
  const [stock, setStock] = useState<Device[]>([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [capacityW, setCapacityW] = useState<number | null>(null);
  const [subsidyPercent, setSubsidyPercent] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const isActivate = mode === "activate" || draftContractId != null;
  const deviceChoices = useMemo(() => {
    const byId = new Map<number, Device>();
    for (const d of devices) byId.set(d.id, d);
    for (const d of stock) {
      if (!byId.has(d.id)) byId.set(d.id, d);
    }
    return Array.from(byId.values());
  }, [devices, stock]);
  const noDeviceChoices = deviceChoices.length === 0 && !stockLoading;
  const stockIds = useMemo(() => new Set(stock.map((d) => d.id)), [stock]);
  const assignedIds = useMemo(() => new Set(devices.map((d) => d.id)), [devices]);

  useEffect(() => {
    if (!open) return;
    const initialDeviceId = devices.length === 1 ? String(devices[0].id) : "";
    setForm({
      device_id: initialDeviceId,
      total_price: "",
      discount_rule_id: "",
      advance_required: "yes",
      initial_deposit: "",
      payment_frequency: "monthly",
      duration_months: "12",
    });
    setCapacityW(null);
    setSubsidyPercent(0);
    setErr("");
    setStock([]);
    api.get<{ discount_rules?: DiscountRuleOpt[]; rules?: DiscountRuleOpt[] }>("/discount-rules")
      .then((d) => {
        const rows = d.discount_rules || d.rules || [];
        setRules(rows.filter((r) => r && typeof r.discount_percent === "number"));
      })
      .catch(() => setRules([]));
    // Root lifecycle: claim unassigned stock in Activate/Create (no Devices hop)
    setStockLoading(true);
    api.get<{ devices?: Device[] }>("/devices?assignment=unassigned&per_page=100")
      .then((d) => setStock(d.devices ?? []))
      .catch(() => setStock([]))
      .finally(() => setStockLoading(false));
    if (initialDeviceId) {
      void onDeviceChange(initialDeviceId);
    }
  }, [open, devices.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDeviceChange = async (deviceId: string) => {
    set("device_id", deviceId);
    if (!deviceId) {
      setCapacityW(null);
      setSubsidyPercent(0);
      return;
    }
    const d = deviceChoices.find((x) => String(x.id) === deviceId)
      || devices.find((x) => String(x.id) === deviceId)
      || stock.find((x) => String(x.id) === deviceId);
    if (!d) return;
    try {
      const detail = await api.get<{
        device?: {
          default_price?: number;
          capacity?: number | string;
          capacity_watts?: number;
          subsidy_percent?: number;
          device_type?: {
            default_price?: number;
            capacity_watts?: number;
            subsidy_percent?: number;
          };
        };
      }>(`/devices/${encodeURIComponent(d.serial_number)}`);
      const body = detail.device || {};
      const dtype = body.device_type || {};
      const price = body.default_price ?? dtype.default_price;
      const rawCap = body.capacity_watts ?? dtype.capacity_watts ?? body.capacity;
      const cap = rawCap != null && rawCap !== "" ? Number(rawCap) : NaN;
      const sub = Number(body.subsidy_percent ?? dtype.subsidy_percent ?? 0) || 0;
      setSubsidyPercent(sub);
      if (Number.isFinite(cap)) setCapacityW(cap);
      else setCapacityW(null);
      if (price != null && !form.total_price) {
        set("total_price", String(price));
      }
      if (form.advance_required === "yes" && Number.isFinite(cap)) {
        const dep = DEPOSIT_BY_CAPACITY[cap];
        if (dep != null && !form.initial_deposit) {
          set("initial_deposit", String(dep));
        }
      }
    } catch {
      /* keep manual entry */
    }
  };

  const selectedRule = rules.find((r) => String(r.id) === form.discount_rule_id);
  const discountPct = selectedRule ? Number(selectedRule.discount_percent) : 0;
  const totalPrice = parseFloat(form.total_price) || 0;
  const afterSubsidy = Math.round(totalPrice * (1 - subsidyPercent / 100) * 100) / 100;
  const finalPrice = Math.round(afterSubsidy * (1 - discountPct / 100) * 100) / 100;
  const advanceRequired = form.advance_required === "yes";
  const deposit = advanceRequired ? (parseFloat(form.initial_deposit) || 0) : 0;
  const months = parseInt(form.duration_months, 10) || 12;
  const financeable = Math.max(Math.round((finalPrice - deposit) * 100) / 100, 0);
  const installment = computeInstallment(finalPrice, months, deposit, form.payment_frequency);

  useEffect(() => {
    if (!open || form.advance_required !== "yes") return;
    if (form.initial_deposit) return;
    if (capacityW != null && DEPOSIT_BY_CAPACITY[capacityW] != null) {
      set("initial_deposit", String(DEPOSIT_BY_CAPACITY[capacityW]));
    }
  }, [open, form.advance_required, capacityW]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.device_id) { setErr("Select a device (assigned or unassigned stock)"); return; }
    if (!form.total_price) { setErr("Total price is required"); return; }
    if (advanceRequired && deposit <= 0) {
      setErr("Enter advance payment amount");
      return;
    }
    if (advanceRequired && deposit > finalPrice) {
      setErr("Advance cannot exceed discounted total");
      return;
    }
    setBusy(true); setErr("");
    const body = {
      device_id: parseInt(form.device_id, 10),
      total_price: totalPrice,
      discount_percent: discountPct,
      discount_rule_id: form.discount_rule_id ? parseInt(form.discount_rule_id, 10) : null,
      advance_required: advanceRequired,
      initial_deposit: deposit,
      payment_frequency: form.payment_frequency,
      payment_period_months: months,
    };
    try {
      if (isActivate && draftContractId != null) {
        await api.post(`/customers/${customerId}/contracts/${draftContractId}/activate`, body);
      } else {
        await api.post(`/customers/${customerId}/contracts`, body);
      }
      onSuccess();
      onClose();
    } catch (ex: unknown) {
      const message = ex instanceof Error ? ex.message : "Failed to create contract";
      setErr(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppModal open={open} onClose={onClose} title={isActivate ? "Activate Contract" : "Create Contract"}>
      <form onSubmit={submit}>
        {err && (
          <div className="pg-inline-error" role="alert" style={{ marginBottom: 12 }}>
            <IconWarning size={14} /> {err}
          </div>
        )}
        {isActivate && (
          <p className="pg-text-muted" style={{ marginBottom: 12 }}>
            Complete KYC draft in one step — pick stock (assigns on save), price, and activate.
          </p>
        )}
        <AppField label="Device">
          <AppSelect
            id="contract-device"
            value={form.device_id}
            onChange={(e) => void onDeviceChange(e.target.value)}
            disabled={busy || stockLoading}
          >
            <option value="">{stockLoading ? "Loading stock…" : "Select device…"}</option>
            {deviceChoices.map((d) => (
              <option key={d.id} value={String(d.id)}>
                {d.serial_number}{" "}
                ({stockIds.has(d.id) && !assignedIds.has(d.id) ? "unassigned stock" : d.status})
              </option>
            ))}
          </AppSelect>
        </AppField>
        {noDeviceChoices && (
          <p className="pg-text-muted" style={{ marginTop: -8, marginBottom: 12 }}>
            No devices on this customer and no unassigned stock. Register stock first.
          </p>
        )}
        <AppField label="Total Price (TZS)">
          <AppInput id="contract-price" type="number" placeholder="e.g. 500000"
            value={form.total_price} onChange={(e) => set("total_price", e.target.value)}
            disabled={busy || noDeviceChoices} autoFocus />
        </AppField>
        <AppField label="Discount">
          <AppSelect id="contract-discount" value={form.discount_rule_id}
            onChange={(e) => set("discount_rule_id", e.target.value)} disabled={busy || noDeviceChoices}>
            <option value="">No discount (0%)</option>
            {rules.map((r) => (
              <option key={r.id} value={String(r.id)}>
                {r.name} — {r.discount_percent}%
              </option>
            ))}
          </AppSelect>
        </AppField>
        <AppField label="Advance payment required?">
          <AppSelect id="contract-advance-req" value={form.advance_required}
            onChange={(e) => {
              set("advance_required", e.target.value);
              if (e.target.value === "no") set("initial_deposit", "");
            }} disabled={busy || noDeviceChoices}>
            <option value="yes">Yes — compulsory advance</option>
            <option value="no">No — installments from full price</option>
          </AppSelect>
        </AppField>
        {advanceRequired && (
          <AppField label="Advance payment (TZS)">
            <AppInput id="contract-deposit" type="number" min="0"
              placeholder={capacityW && DEPOSIT_BY_CAPACITY[capacityW]
                ? `Default ${DEPOSIT_BY_CAPACITY[capacityW].toLocaleString()}`
                : "e.g. 50000"}
              value={form.initial_deposit}
              onChange={(e) => set("initial_deposit", e.target.value)}
              disabled={busy || noDeviceChoices} />
          </AppField>
        )}
        <AppField label="Payment Frequency">
          <AppSelect id="contract-freq" value={form.payment_frequency}
            onChange={(e) => set("payment_frequency", e.target.value)} disabled={busy || noDeviceChoices}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </AppSelect>
        </AppField>
        <AppField label="Duration (months)">
          <AppInput id="contract-duration" type="number" min="1" max="60"
            value={form.duration_months} onChange={(e) => set("duration_months", e.target.value)}
            disabled={busy || noDeviceChoices} />
        </AppField>

        {totalPrice > 0 && (
          <div className="pg-card" style={{ padding: 12, marginBottom: 12, background: "var(--pg-surface-muted, #f6f7f8)" }}>
            <p className="pg-text-muted" style={{ margin: 0 }}>Contract preview</p>
            <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
              {subsidyPercent > 0 && (
                <li>Subsidy: {subsidyPercent}% → TZS {afterSubsidy.toLocaleString()}</li>
              )}
              <li>Discount: {discountPct}% → final <strong>TZS {finalPrice.toLocaleString()}</strong></li>
              {advanceRequired && (
                <li>Advance: <strong>TZS {deposit.toLocaleString()}</strong></li>
              )}
              <li>Remaining after advance: <strong>TZS {financeable.toLocaleString()}</strong></li>
              <li>
                {form.payment_frequency === "monthly" ? "Monthly" :
                  form.payment_frequency === "weekly" ? "Weekly" : "Daily"} payment:{" "}
                <strong>TZS {installment.toLocaleString()}</strong> over {months} months
              </li>
              {advanceRequired ? (
                <li>First unlock: <strong>30 days</strong> after advance payment</li>
              ) : (
                <li>Days follow installment allocation (no forced 30-day advance unlock)</li>
              )}
            </ul>
          </div>
        )}

        <div className="pg-modal-actions">
          <AppButton type="submit" disabled={busy || noDeviceChoices || !form.device_id}>
            {busy
              ? <><IconSpinner size={14}/> {isActivate ? "Activating…" : "Creating…"}</>
              : (isActivate ? "Activate Contract" : "Create Contract")}
          </AppButton>
          <AppButton type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</AppButton>
        </div>
      </form>
    </AppModal>
  );
}

type CustomerProfileForm = {
  first_name: string;
  last_name: string;
  phone: string;
  address: string;
  village: string;
  ward: string;
  district: string;
  region: string;
  area_code: string;
};

function EditProfileModal({
  customer,
  open,
  onClose,
  onSuccess,
}: {
  customer: CustomerData;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [form, setForm] = useState<CustomerProfileForm>({
    first_name: "", last_name: "", phone: "", address: "", village: "",
    ward: "", district: "", region: "", area_code: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (key: keyof CustomerProfileForm, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    if (!open) return;
    setForm({
      first_name: customer.first_name ?? "",
      last_name: customer.last_name ?? "",
      phone: customer.phone ?? "",
      address: customer.address ?? "",
      village: customer.village ?? "",
      ward: customer.ward ?? "",
      district: customer.district ?? "",
      region: customer.region ?? "",
      area_code: customer.area_code ?? "",
    });
    setErr("");
  }, [customer, open]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.phone.trim()) {
      setErr("First name, last name, and phone are required");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.put(`/customers/${customer.id}`, Object.fromEntries(
        Object.entries(form).map(([key, value]) => [key, value.trim() || null]),
      ));
      await onSuccess();
      onClose();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : "Failed to update customer profile");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppModal open={open} onClose={onClose} title="Edit customer profile">
      <form onSubmit={submit}>
        {err && <div className="pg-inline-error" role="alert">{err}</div>}
        <div className="pg-two-col-form">
          <AppField label="First name">
            <AppInput id="customer-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} disabled={busy} autoFocus />
          </AppField>
          <AppField label="Last name">
            <AppInput id="customer-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} disabled={busy} />
          </AppField>
        </div>
        <AppField label="Phone">
          <AppInput id="customer-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} disabled={busy} />
        </AppField>
        <AppField label="Address">
          <AppInput id="customer-address" value={form.address} onChange={(e) => set("address", e.target.value)} disabled={busy} />
        </AppField>
        <div className="pg-two-col-form">
          <AppField label="Village">
            <AppInput id="customer-village" value={form.village} onChange={(e) => set("village", e.target.value)} disabled={busy} />
          </AppField>
          <AppField label="Ward">
            <AppInput id="customer-ward" value={form.ward} onChange={(e) => set("ward", e.target.value)} disabled={busy} />
          </AppField>
        </div>
        <div className="pg-two-col-form">
          <AppField label="District">
            <AppInput id="customer-district" value={form.district} onChange={(e) => set("district", e.target.value)} disabled={busy} />
          </AppField>
          <AppField label="Region">
            <AppInput id="customer-region" value={form.region} onChange={(e) => set("region", e.target.value)} disabled={busy} />
          </AppField>
        </div>
        <AppField label="Area code">
          <AppInput id="customer-area-code" value={form.area_code} onChange={(e) => set("area_code", e.target.value)} disabled={busy} />
        </AppField>
        <div className="pg-modal-actions">
          <AppButton type="submit" disabled={busy}>
            {busy ? <><IconSpinner size={14}/> Saving…</> : "Save profile"}
          </AppButton>
          <AppButton type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</AppButton>
        </div>
      </form>
    </AppModal>
  );
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────

function OverviewTab({ customer }: { customer: CustomerData }) {
  const activeDevice = deriveActiveDevice(customer.devices);

  return (
    <div className="pg-two-col">
      <AppCard title="Profile">
        <AppTable
          head={["Field", "Value"]}
          rows={[
            ["Customer #", customer.customer_number],
            ["Full Name", `${customer.first_name} ${customer.last_name}`],
            ["Phone", customer.phone],
            ["National ID", customer.national_id ?? "—"],
            ["Email", customer.email ?? "—"],
            ["Address", customer.address ?? "—"],
            ["Ward", customer.ward ?? "—"],
            ["District", customer.district ?? "—"],
            ["Region", customer.region ?? "—"],
            ["Registered", new Date(customer.created_at).toLocaleDateString("en-GB")],
            ...(customer.source_lead
              ? [[
                  "Source lead",
                  <Link
                    key="source-lead"
                    to={`/field/leads/${customer.source_lead.id}/duplicates`}
                  >
                    #{customer.source_lead.id}
                    {customer.source_lead.status ? ` · ${customer.source_lead.status}` : ""}
                  </Link>,
                ]]
              : []),
          ]}
        />
      </AppCard>
      <AppCard title="Active Device & Contract">
        {activeDevice ? (
          <>
            <p className="pg-text-muted">Device</p>
            <AppTable
              head={["Field", "Value"]}
              rows={[
                ["Serial", <Link to={`/devices/${activeDevice.serial_number}`}>{activeDevice.serial_number}</Link>],
                ["Status", activeDevice.status.toUpperCase()],
                ["Locked", activeDevice.is_locked ? "Yes" : "No"],
              ]}
            />
          </>
        ) : (
          <p className="pg-text-muted">No device currently assigned.</p>
        )}
        {customer.active_contract ? (
          <>
            <p className="pg-text-muted" style={{ marginTop: 16 }}>Contract</p>
            <AppTable
              head={["Field", "Value"]}
              rows={[
                ["Contract #", customer.active_contract.contract_number],
                ["Status", customer.active_contract.is_completed
                  ? <AppBadge tone="neutral">Completed</AppBadge>
                  : <AppBadge tone="success">Active</AppBadge>
                ],
                ["Total Price", `TZS ${fmtMoney(customer.active_contract.total_price)}`],
                ["Final Price", `TZS ${fmtMoney(customer.active_contract.final_price ?? customer.active_contract.total_price)}`],
                ["Discount", `${customer.active_contract.discount_percent ?? 0}%`],
                ["Advance required", customer.active_contract.advance_required === false ? "No" : "Yes"],
                ["Advance / deposit", `TZS ${fmtMoney(customer.active_contract.initial_deposit)}`],
                ["Paid", `TZS ${fmtMoney(customer.active_contract.amount_paid)}`],
                ["Remaining", `TZS ${fmtMoney(customer.active_contract.amount_remaining)}`],
                ["Arrears", (customer.active_contract.arrears ?? 0) > 0
                  ? <AppBadge tone="error"><IconWarning size={12}/> TZS {fmtMoney(customer.active_contract.arrears)}</AppBadge>
                  : <AppBadge tone="success"><IconCheck size={12}/> None</AppBadge>
                ],
                ["Period (months)", customer.active_contract.payment_period_months ?? "—"],
              ]}
            />
          </>
        ) : customer.draft_contract ? (
          <p className="pg-text-muted" style={{ marginTop: 16 }}>
            Draft contract {customer.draft_contract.contract_number} awaiting activation (device + pricing).
          </p>
        ) : (
          <p className="pg-text-muted" style={{ marginTop: 16 }}>No active contract.</p>
        )}
      </AppCard>
    </div>
  );
}

function ContractsTab({ customerId }: { customerId: number }) {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get<{ contracts: Contract[] }>(`/customers/${customerId}/contracts`)
      .then((d) => { setContracts(d.contracts); setLoading(false); })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Failed to load contracts");
        setLoading(false);
      });
  }, [customerId]);

  if (loading) return <div className="pg-state"><IconSpinner size={32}/><p>Loading…</p></div>;
  if (error) return <div className="pg-state"><IconError size={32}/><p>{error}</p></div>;
  if (!contracts.length) return <div className="pg-state"><IconInfo size={32}/><p>No contracts yet.</p></div>;

  return (
    <AppCard>
      <AppTable
        head={["Contract #", "Status", "Final (TZS)", "Discount", "Advance", "Paid (TZS)", "Remaining (TZS)", "Arrears", "Frequency", "Created"]}
        rows={contracts.map((c) => [
          c.contract_number,
          <AppBadge tone={c.is_completed ? "neutral" : c.is_draft ? "warning" : "success"}>
            {c.is_completed ? "Completed" : c.is_draft ? "Draft" : "Active"}
          </AppBadge>,
          fmtMoney(c.final_price ?? c.total_price),
          `${c.discount_percent ?? 0}%`,
          c.advance_required === false
            ? "No"
            : `Yes · ${fmtMoney(c.initial_deposit)}`,
          fmtMoney(c.amount_paid),
          fmtMoney(c.amount_remaining),
          (c.arrears ?? 0) > 0 ? <AppBadge tone="error">TZS {fmtMoney(c.arrears)}</AppBadge> : "—",
          c.payment_frequency ?? "—",
          new Date(c.created_at).toLocaleDateString("en-GB"),
        ])}
      />
    </AppCard>
  );
}

function PaymentsTab({ customerId }: { customerId: number }) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Prefer canonical customer payments path (nginx → payment-service under split).
        // Fall back to payment list if the gateway still routes to empty customer-service ORM.
        let rows: Payment[] = [];
        try {
          const d = await api.get<{ payments: Payment[] }>(`/customers/${customerId}/payments`);
          rows = d.payments ?? [];
        } catch {
          rows = [];
        }
        if (rows.length === 0) {
          const d2 = await api.get<{ payments: Payment[] }>(
            `/payments?customer_id=${customerId}&per_page=100`,
          );
          rows = d2.payments ?? [];
        }
        if (!cancelled) {
          setPayments(rows);
          setLoading(false);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load payments");
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  const methodLabel: Record<string, string> = {
    mobile_money: "Mobile Money", cash: "Cash", ussd: "USSD", mobile_app: "App",
  };

  const paymentTone = (status: string): "success" | "error" | "neutral" => {
    const s = status.toLowerCase();
    if (s === "completed") return "success";
    if (s === "failed") return "error";
    return "neutral";
  };

  if (loading) return <div className="pg-state"><IconSpinner size={32}/><p>Loading…</p></div>;
  if (error) return <div className="pg-state"><IconError size={32}/><p>{error}</p></div>;
  if (!payments.length) return <div className="pg-state"><IconInfo size={32}/><p>No payments recorded yet.</p></div>;

  return (
    <AppCard>
      <AppTable
        head={["Reference", "Amount (TZS)", "Token / progress", "Method", "Status", "Date"]}
        rows={payments.map((p) => {
          const days = p.service_days ?? p.token_days;
          const shortfallTzs =
            p.remaining_for_token_tzs ??
            (p.shortfall_cents != null ? p.shortfall_cents / 100 : null);
          let tokenCell: string;
          if (days != null && days > 0) {
            tokenCell = `${days} day${days === 1 ? "" : "s"}`;
          } else if (shortfallTzs != null && shortfallTzs > 0) {
            tokenCell = `Need TZS ${Math.round(shortfallTzs).toLocaleString()} more`;
          } else if (days === 0) {
            tokenCell = "No token";
          } else {
            tokenCell = "—";
          }
          return [
            p.payment_reference,
            fmtMoney(p.amount),
            tokenCell,
            methodLabel[p.method] ?? p.method,
            <AppBadge tone={paymentTone(p.status)}>{p.status}</AppBadge>,
            new Date(p.created_at).toLocaleDateString("en-GB"),
          ];
        })}
      />
    </AppCard>
  );
}

function DmrvTab({ customerId }: { customerId: number }) {
  const [steps, setSteps] = useState<DMRVStep[]>([]);
  const [dmrvStatus, setDmrvStatus] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get<{ steps: DMRVStep[]; dmrv_status: string }>(`/customers/${customerId}/dmrv`)
      .then((d) => {
        setSteps(d.steps);
        setDmrvStatus(d.dmrv_status);
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Failed to load dMRV");
        setLoading(false);
      });
  }, [customerId]);

  const stepTone = (s: string): "success"|"warning"|"error"|"neutral" =>
    ({ approved: "success", rejected: "error", pending: "neutral" }[s] as "success"|"warning"|"error"|"neutral") ?? "neutral";

  if (loading) return <div className="pg-state"><IconSpinner size={32}/><p>Loading…</p></div>;
  if (error) return <div className="pg-state"><IconError size={32}/><p>{error}</p></div>;

  return (
    <AppCard>
      {dmrvStatus && (
        <p style={{ marginBottom: 16 }}>
          Overall status: <AppBadge tone={dmrvStatus === "verified" ? "success" : dmrvStatus === "blocked" ? "error" : "info"}>
            {dmrvStatus.replace("_", " ").toUpperCase()}
          </AppBadge>
        </p>
      )}
      <div className="pg-dmrv-steps">
        {steps.map((step) => (
          <div key={step.step} className={`pg-dmrv-step status-${step.status}`}>
            <div className="step-num">{step.step}</div>
            <div className="step-body">
              <div className="step-label">{step.label}</div>
              <div className="step-meta">
                <AppBadge tone={stepTone(step.status)}>{step.status.toUpperCase()}</AppBadge>
                {step.actioned_by_id != null && (
                  <span className="pg-text-muted">by user #{step.actioned_by_id}</span>
                )}
                {step.actioned_at && (
                  <span className="pg-text-muted">{new Date(step.actioned_at).toLocaleDateString("en-GB")}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </AppCard>
  );
}

function CreditTab({ customerId }: { customerId: number }) {
  const [credit, setCredit] = useState<{
    score: number;
    band: string;
    factors: { key: string; label: string; weight: number; raw_0_1: number; contribution: number }[];
    payments_scored?: number;
    missed_installments?: number;
    calculated_at?: string | null;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get<{ credit: typeof credit }>(`/customers/${customerId}/credit`)
      .then((d) => {
        setCredit(d.credit);
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Failed to load credit profile");
        setLoading(false);
      });
  }, [customerId]);

  if (loading) return <div className="pg-state"><IconSpinner size={32}/><p>Loading…</p></div>;
  if (error) return <div className="pg-state"><IconError size={32}/><p>{error}</p></div>;
  if (!credit) return <div className="pg-state"><IconInfo size={32}/><p>No credit profile yet.</p></div>;

  const bandTone =
    credit.band === "excellent" || credit.band === "very_good" || credit.band === "good"
      ? "success"
      : credit.band === "poor"
        ? "error"
        : "warning";

  return (
    <div className="pg-two-col">
      <AppCard title="Internal credit score">
        <p style={{ fontSize: 36, fontWeight: 700, margin: "8px 0" }}>{credit.score}</p>
        <AppBadge tone={bandTone as "success" | "error" | "warning"}>{credit.band.replace("_", " ").toUpperCase()}</AppBadge>
        <p className="pg-text-muted" style={{ marginTop: 12 }}>
          Behavioural score from payment events only (not a CRB bureau pull).
          Range 300–850.
        </p>
        <AppTable
          head={["Metric", "Value"]}
          rows={[
            ["Payments scored", String(credit.payments_scored ?? 0)],
            ["Missed installments", String(credit.missed_installments ?? 0)],
            ["Last calculated", credit.calculated_at
              ? new Date(credit.calculated_at).toLocaleString("en-GB")
              : "—"],
          ]}
        />
      </AppCard>
      <AppCard title="Factor breakdown">
        <AppTable
          head={["Factor", "Weight", "Strength", "Contribution"]}
          rows={(credit.factors ?? []).map((f) => [
            f.label,
            `${Math.round((f.weight ?? 0) * 100)}%`,
            `${Math.round((f.raw_0_1 ?? 0) * 100)}%`,
            String(f.contribution ?? "—"),
          ])}
        />
      </AppCard>
    </div>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────────

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [pageState, setPageState] = useState<"loading"|"error"|"success">("loading");
  const [pageError, setPageError] = useState("");
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [deactivateBusy, setDeactivateBusy] = useState(false);
  const [mutationError, setMutationError] = useState("");
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState<{ tone: "success"|"error"; text: string } | null>(null);
  const [confirmPush, setConfirmPush] = useState(false);

  const load = async () => {
    setPageState("loading");
    try {
      const d = await api.get<{ customer: CustomerData }>(`/customers/${id}`);
      setCustomer(d.customer);
      setPageState("success");
    } catch (ex: unknown) {
      const message = ex instanceof Error ? ex.message : "Customer not found";
      setPageError(message);
      setPageState("error");
    }
  };

  useEffect(() => { load(); }, [id]);

  // Field journey deep-link: /customers/:id?contract=activate
  useEffect(() => {
    if (pageState !== "success" || !customer) return;
    if (searchParams.get("contract") !== "activate") return;
    if (customer.active_contract) return;
    setContractModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("contract");
    setSearchParams(next, { replace: true });
  }, [pageState, customer, searchParams, setSearchParams]);

  const activeDevice = useMemo(
    () => (customer ? deriveActiveDevice(customer.devices ?? []) : null),
    [customer]
  );

  const pushToRea = async () => {
    if (!customer) return;
    setPushBusy(true);
    setPushMsg(null);
    try {
      await api.post(`/customers/${customer.id}/prospect/push`, {});
      setPushMsg({ tone: "success", text: "Successfully pushed to REA Prospect API." });
    } catch (ex: unknown) {
      const message = ex instanceof Error ? ex.message : "Failed to push to REA";
      setPushMsg({ tone: "error", text: message });
    } finally {
      setPushBusy(false);
      setConfirmPush(false);
    }
  };

  const deactivateCustomer = async () => {
    if (!customer) return;
    setDeactivateBusy(true);
    setMutationError("");
    try {
      await api.post(`/customers/${customer.id}/deactivate`, {});
      setConfirmDeactivate(false);
      await load();
    } catch (ex: unknown) {
      setMutationError(ex instanceof Error ? ex.message : "Failed to deactivate customer");
    } finally {
      setDeactivateBusy(false);
    }
  };

  if (pageState === "loading") return (
    <div className="pg-state"><span className="state-icon info"><IconSpinner size={40}/></span><p>Loading customer…</p></div>
  );

  if (pageState === "error" || !customer) return (
    <div className="pg-state">
      <span className="state-icon error"><IconError size={40}/></span>
      <h2>Customer not found</h2><p>{pageError}</p>
      <div className="pg-state-actions">
        <AppButton type="button" onClick={load}>Retry</AppButton>
        <Link to="/customers"><AppButton type="button" variant="secondary">← Customers</AppButton></Link>
      </div>
    </div>
  );

  const TABS: { id: Tab; label: string }[] = [
    { id: "overview",  label: "Overview" },
    { id: "contracts", label: "Contracts" },
    { id: "payments",  label: "Payments" },
    { id: "credit",    label: "Credit" },
    { id: "dmrv",      label: "dMRV Steps" },
  ];

  const hasActiveContract = !!customer.active_contract;
  const draftId = customer.draft_contract?.id ?? null;
  const contractBtnLabel = hasActiveContract
    ? "Contract Active"
    : draftId != null
      ? "Activate Contract"
      : "Create Contract";
  const user = getUser();
  const canSms = hasPermission(user, "sms.send");
  const entityParams = { customer_id: customer.id };

  return (
    <>
      {contractModalOpen && (
        <CreateContractModal
          customerId={customer.id}
          devices={customer.devices ?? []}
          open={contractModalOpen}
          onClose={() => setContractModalOpen(false)}
          onSuccess={load}
          draftContractId={draftId}
          mode={draftId != null ? "activate" : "create"}
        />
      )}
      {editProfileOpen && (
        <EditProfileModal
          customer={customer}
          open={editProfileOpen}
          onClose={() => setEditProfileOpen(false)}
          onSuccess={load}
        />
      )}

      {/* Breadcrumb */}
      <nav className="pg-breadcrumb" aria-label="Breadcrumb">
        <Link to="/customers">Customers</Link>
        <IconChevronRight size={12}/>
        <span>{customer.first_name} {customer.last_name}</span>
      </nav>

      {/* Header */}
      <AppCard>
        <div className="pg-device-header">
          <div className="pg-device-header__title">
            <h2>{customer.first_name} {customer.last_name}</h2>
            <p className="pg-text-muted">{customer.customer_number} · {customer.phone}</p>
          </div>
          <div className="pg-device-header__badges">
            <AppBadge tone={customer.is_active ? "success" : "neutral"}>
              {customer.is_active ? <><IconCheck size={12}/> Active</> : "Inactive"}
            </AppBadge>
            <AppBadge tone={customer.dmrv_status === "verified" ? "success" : customer.dmrv_status === "blocked" ? "error" : "info"}>
              dMRV: {(customer.dmrv_status ?? "pending").replace("_", " ").toUpperCase()}
            </AppBadge>
          </div>
        </div>
        {pushMsg && (
          <p style={{ marginTop: 8 }}>
            <AppBadge tone={pushMsg.tone}>{pushMsg.text}</AppBadge>
          </p>
        )}
        {mutationError && (
          <div className="pg-inline-error" role="alert" style={{ marginTop: 8 }}>
            <IconWarning size={14}/> {mutationError}
          </div>
        )}
        <div className="pg-action-bar">
          <AppButton type="button" variant="secondary" onClick={() => { setMutationError(""); setEditProfileOpen(true); }}>
            Edit profile
          </AppButton>
          {customer.is_active && (
            <AppButton type="button" variant="secondary" onClick={() => { setMutationError(""); setConfirmDeactivate(true); }}>
              Deactivate
            </AppButton>
          )}
          <AppButton
            type="button"
            id="btn-create-contract"
            onClick={() => setContractModalOpen(true)}
            disabled={hasActiveContract}
          >
            {contractBtnLabel}
          </AppButton>
          <Link to={withEntityQuery("/field/payments/new", entityParams)}>
            <AppButton type="button" variant="secondary">Record Payment</AppButton>
          </Link>
          <Link to={withEntityQuery("/installations/new", entityParams)}>
            <AppButton type="button" variant="secondary">Schedule Install</AppButton>
          </Link>
          <Link to={withEntityQuery("/tickets", entityParams)}>
            <AppButton type="button" variant="secondary">New Ticket</AppButton>
          </Link>
          {canSms ? (
            <Link to={withEntityQuery("/sms-center", entityParams)}>
              <AppButton type="button" variant="secondary">SMS</AppButton>
            </Link>
          ) : null}
          {activeDevice && (
            <Link to={`/devices/${activeDevice.serial_number}`}>
              <AppButton type="button" variant="secondary">View Device</AppButton>
            </Link>
          )}
          <AppButton
            type="button"
            variant="secondary"
            onClick={() => setConfirmPush(true)}
            disabled={pushBusy || customer.dmrv_status !== "verified"}
            title={customer.dmrv_status !== "verified" ? "dMRV must be verified before pushing to REA" : undefined}
          >
            {pushBusy ? <><IconSpinner size={14}/> Pushing…</> : "Push to REA"}
          </AppButton>
        </div>
      </AppCard>

      {/* Tabs */}
      <div className="pg-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`pg-tab${activeTab === t.id ? " active" : ""}`}
            onClick={() => setActiveTab(t.id)}
          >{t.label}</button>
        ))}
      </div>

      {activeTab === "overview"  && <OverviewTab customer={customer} />}
      {activeTab === "contracts" && <ContractsTab customerId={customer.id} />}
      {activeTab === "payments"  && <PaymentsTab  customerId={customer.id} />}
      {activeTab === "credit"    && <CreditTab    customerId={customer.id} />}
      {activeTab === "dmrv"      && <DmrvTab      customerId={customer.id} />}

      <ConfirmAction
        open={confirmDeactivate}
        title="Deactivate customer?"
        message={`${customer.first_name} ${customer.last_name} will no longer be active. Existing records are retained.`}
        confirmLabel="Deactivate"
        danger
        busy={deactivateBusy}
        onCancel={() => setConfirmDeactivate(false)}
        onConfirm={() => void deactivateCustomer()}
      />

      <ConfirmAction
        open={confirmPush}
        title="Push to REA Prospect?"
        message="Submits verified installation data to the external Prospect API. Confirm only when dMRV is complete."
        confirmLabel="Push to REA"
        danger
        busy={pushBusy}
        onCancel={() => setConfirmPush(false)}
        onConfirm={() => void pushToRea()}
      />
    </>
  );
}
